/**
 * Risk Approval Page - עמוד אישור רמת סיכון
 * After completing the questionnaire, the client reviews their recommended risk level,
 * can adjust ±1, and signs the risk approval document.
 * Responsive: mobile-first with two-column desktop layout.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { AlertCircle, Check, Phone, ShieldCheck, TrendingUp, ArrowDown, ArrowUp } from 'lucide-react'
import api from '../services/api'

const RISK_PROFILES: Record<number, { name: string; description: string; color: string; icon: 'shield' | 'trending' }> = {
  1: { name: 'שמרני', description: 'חשיפה נמוכה לשוק המניות, עדיפות לאגרות חוב ומכשירים סולידיים', color: 'bg-green-100 text-green-800 border-green-300', icon: 'shield' },
  2: { name: 'שמרני-מתון', description: 'חשיפה מוגבלת למניות, רוב התיק באגרות חוב', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: 'shield' },
  3: { name: 'מאוזן', description: 'חלוקה מאוזנת בין מניות לאגרות חוב', color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: 'trending' },
  4: { name: 'צמיחה', description: 'חשיפה גבוהה למניות, מיעוט באגרות חוב', color: 'bg-orange-100 text-orange-800 border-orange-300', icon: 'trending' },
  5: { name: 'אגרסיבי', description: 'חשיפה מרבית למניות ונכסי סיכון', color: 'bg-red-100 text-red-800 border-red-300', icon: 'trending' },
}

// Onboarding stepper
const ONBOARDING_STEPS = [
  { label: 'פרטים', key: 'details' },
  { label: 'הסכם', key: 'agreement' },
  { label: 'שאלון', key: 'questionnaire' },
  { label: 'אישור סיכון', key: 'risk-approval' },
]

function OnboardingStepper() {
  const currentIndex = 3
  return (
    <div className="flex items-center justify-center gap-0 mb-4 lg:mb-6" dir="ltr">
      {ONBOARDING_STEPS.map((step, index) => {
        const isCompleted = index < currentIndex
        const isCurrent = index === currentIndex
        return (
          <div key={step.key} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-7 h-7 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                  isCompleted
                    ? 'bg-green-500 border-green-500 text-white'
                    : isCurrent
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-white border-gray-300 text-gray-400'
                }`}
              >
                {isCompleted ? <Check size={12} strokeWidth={3} aria-hidden="true" /> : index + 1}
              </div>
              <span className={`mt-0.5 text-[10px] lg:text-xs font-medium whitespace-nowrap ${
                isCompleted ? 'text-green-600' : isCurrent ? 'text-blue-700' : 'text-gray-400'
              }`}>
                {step.label}
              </span>
            </div>
            {index < ONBOARDING_STEPS.length - 1 && (
              <div className={`h-0.5 w-10 lg:w-14 mx-1 mb-3.5 transition-all ${
                index < currentIndex ? 'bg-green-400' : 'bg-gray-200'
              }`} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Signature Pad ────────────────────────────────────────────────────────────
function SignaturePad({
  onSignatureChange,
  isEmpty,
  setIsEmpty,
}: {
  onSignatureChange: (dataUrl: string) => void
  isEmpty: boolean
  setIsEmpty: (v: boolean) => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const lastPos = useRef<{ x: number; y: number } | null>(null)

  const getPos = (e: { clientX: number; clientY: number }, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    }
  }

  const startDraw = useCallback((e: MouseEvent | TouchEvent) => {
    e.preventDefault()
    const canvas = canvasRef.current!
    drawing.current = true
    const point = e instanceof TouchEvent
      ? getPos(e.touches[0], canvas)
      : getPos(e as MouseEvent, canvas)
    lastPos.current = point
  }, [])

  const draw = useCallback((e: MouseEvent | TouchEvent) => {
    e.preventDefault()
    if (!drawing.current) return
    const canvas = canvasRef.current!
    const ctx = canvas.getContext('2d')!
    const point = e instanceof TouchEvent
      ? getPos(e.touches[0], canvas)
      : getPos(e as MouseEvent, canvas)

    ctx.beginPath()
    ctx.moveTo(lastPos.current!.x, lastPos.current!.y)
    ctx.lineTo(point.x, point.y)
    ctx.strokeStyle = '#1e3a5f'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke()

    lastPos.current = point
    setIsEmpty(false)
    onSignatureChange(canvas.toDataURL('image/png'))
  }, [onSignatureChange, setIsEmpty])

  const endDraw = useCallback(() => {
    drawing.current = false
    lastPos.current = null
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current!
    canvas.addEventListener('mousedown', startDraw)
    canvas.addEventListener('mousemove', draw)
    canvas.addEventListener('mouseup', endDraw)
    canvas.addEventListener('mouseleave', endDraw)
    canvas.addEventListener('touchstart', startDraw, { passive: false })
    canvas.addEventListener('touchmove', draw, { passive: false })
    canvas.addEventListener('touchend', endDraw)
    return () => {
      canvas.removeEventListener('mousedown', startDraw)
      canvas.removeEventListener('mousemove', draw)
      canvas.removeEventListener('mouseup', endDraw)
      canvas.removeEventListener('mouseleave', endDraw)
      canvas.removeEventListener('touchstart', startDraw)
      canvas.removeEventListener('touchmove', draw)
      canvas.removeEventListener('touchend', endDraw)
    }
  }, [startDraw, draw, endDraw])

  const clear = () => {
    const canvas = canvasRef.current!
    const ctx = canvas.getContext('2d')!
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setIsEmpty(true)
    onSignatureChange('')
  }

  return (
    <div>
      <div className="relative border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 overflow-hidden"
           style={{ touchAction: 'none' }}>
        <canvas
          ref={canvasRef}
          width={700}
          height={160}
          className="w-full block cursor-crosshair"
          style={{ height: '160px' }}
          role="img"
          aria-label="תיבת חתימה בכתב יד (לא חובה — ניתן להזין שם מלא כחלופה)"
        />
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
            <p className="text-gray-400 text-sm">חתום/י כאן עם העכבר או האצבע</p>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={clear}
        className="mt-2 text-sm text-gray-500 hover:text-red-500 underline transition-colors"
      >
        נקה חתימה
      </button>
    </div>
  )
}

// ── Risk level bar visual ────────────────────────────────────────────────────
function RiskBar({ score }: { score: number }) {
  const segments = [
    { color: 'bg-green-400', label: '1' },
    { color: 'bg-blue-400', label: '2' },
    { color: 'bg-yellow-400', label: '3' },
    { color: 'bg-orange-400', label: '4' },
    { color: 'bg-red-400', label: '5' },
  ]
  return (
    <div className="flex gap-1 items-end h-10" dir="ltr">
      {segments.map((seg, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
          <div
            className={`w-full rounded-sm transition-all ${seg.color} ${
              i + 1 === score ? 'opacity-100 ring-2 ring-offset-1 ring-blue-600' : i + 1 <= score ? 'opacity-60' : 'opacity-20'
            }`}
            style={{ height: `${12 + i * 5}px` }}
          />
          <span className={`text-[9px] font-medium ${i + 1 === score ? 'text-blue-700 font-bold' : 'text-gray-400'}`}>
            {seg.label}
          </span>
        </div>
      ))}
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function RiskApprovalPage() {
  const navigate = useNavigate()
  const { isAuthenticated, user, updateUser } = useAuthStore()

  const [isLoading, setIsLoading] = useState(true)
  const [recommendedScore, setRecommendedScore] = useState<number>(0)
  const [selectedScore, setSelectedScore] = useState<number>(0)
  const [questionnaireId, setQuestionnaireId] = useState<string | null>(null)
  const [error, setError] = useState('')

  // Signature state
  const [signatureDataUrl, setSignatureDataUrl] = useState('')
  const [signatureEmpty, setSignatureEmpty] = useState(true)
  const [typedName, setTypedName] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  // Load risk approval status
  useEffect(() => {
    if (!isAuthenticated) return
    api.get('/risk-approval/status')
      .then((res) => {
        if (res.data.alreadyApproved) {
          updateUser({ riskApprovalSigned: true, approvedRiskScore: res.data.approvedScore })
          navigate('/chat', { replace: true })
          return
        }
        setRecommendedScore(res.data.recommendedScore)
        setSelectedScore(res.data.recommendedScore)
        setQuestionnaireId(res.data.questionnaireId)
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'שגיאה בטעינת נתוני אישור סיכון')
      })
      .finally(() => setIsLoading(false))
  }, [isAuthenticated])

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />
  }

  if (user.riskApprovalSigned === true) {
    return <Navigate to="/chat" replace />
  }

  const adjustment = selectedScore - recommendedScore
  const canAdjust = (score: number) => Math.abs(score - recommendedScore) <= 1 && score >= 1 && score <= 5
  const selectedProfile = RISK_PROFILES[selectedScore]

  const handleSign = async () => {
    if (!typedName.trim()) {
      setError('יש להזין שם מלא')
      return
    }
    if (signatureEmpty && !typedName.trim()) {
      setError('יש לחתום בתיבת החתימה או להזין שם מלא כחלופה')
      return
    }
    if (!confirmed) {
      setError('יש לאשר את ההצהרה')
      return
    }
    setError('')
    setIsSubmitting(true)

    try {
      const res = await api.post('/risk-approval/sign', {
        approvedScore: selectedScore,
        signature: typedName.trim(),
        signatureImage: signatureDataUrl,
        questionnaireId,
        confirmed,
      })

      updateUser({
        riskApprovalSigned: true,
        approvedRiskScore: res.data.approvedScore,
        currentRiskScore: res.data.approvedScore,
      })
      navigate('/chat', { replace: true })
    } catch (err: any) {
      if (err.response?.status === 409) {
        updateUser({ riskApprovalSigned: true })
        navigate('/chat', { replace: true })
        return
      }
      setError(err.response?.data?.error || 'שגיאה בחתימת אישור רמת סיכון')
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Loading ──────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-20" dir="rtl">
        <div className="flex flex-col items-center gap-3">
          <div className="spinner w-10 h-10" />
          <p className="text-gray-600 text-sm">טוען נתוני אישור סיכון...</p>
        </div>
      </div>
    )
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-gray-50 flex flex-col overflow-hidden z-20" dir="rtl">
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 lg:p-8 max-w-5xl mx-auto w-full">

          <OnboardingStepper />

          {/* Header */}
          <div className="bg-blue-900 text-white rounded-t-xl p-5 lg:p-6 text-center">
            <h1 className="text-xl lg:text-2xl font-bold">אישור רמת סיכון</h1>
            <p className="text-blue-200 text-sm lg:text-base mt-1">סקור את רמת הסיכון המומלצת ואשר בחתימתך</p>
          </div>

          <div className="bg-white shadow-lg rounded-b-xl">

            {/* ── Desktop: two-column / Mobile: single-column ── */}
            <div className="lg:grid lg:grid-cols-2 lg:divide-x lg:divide-x-reverse lg:divide-gray-200">

              {/* ═══ LEFT COLUMN (desktop) / TOP (mobile): Risk Level ═══ */}
              <div className="p-5 lg:p-8 space-y-5 lg:space-y-6">

                {/* Recommended Score */}
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-2">רמת הסיכון המומלצת על סמך השאלון</p>
                  <div className="inline-flex items-center gap-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl px-8 py-4 lg:px-10 lg:py-5">
                    <span className="text-5xl lg:text-6xl font-bold text-indigo-600">{recommendedScore}</span>
                    <span className="text-xl lg:text-2xl text-gray-400">/5</span>
                  </div>
                  <p className="text-base lg:text-lg font-semibold text-gray-800 mt-3">
                    {RISK_PROFILES[recommendedScore]?.name}
                  </p>
                  <p className="text-sm text-gray-500 mt-1 max-w-xs mx-auto">
                    {RISK_PROFILES[recommendedScore]?.description}
                  </p>
                </div>

                {/* Visual bar */}
                <div className="max-w-[200px] mx-auto">
                  <RiskBar score={recommendedScore} />
                </div>

                {/* Risk Level Selector */}
                <div className="border border-gray-200 rounded-xl p-4 lg:p-5">
                  <h3 className="font-bold text-gray-800 text-sm lg:text-base mb-2">בחר את רמת הסיכון שלך</h3>
                  <p className="text-xs lg:text-sm text-gray-500 mb-3">ניתן להתאים עד דרגה אחת למעלה או למטה</p>

                  <div className="flex gap-2 lg:gap-3 justify-center">
                    {[1, 2, 3, 4, 5].map((score) => {
                      const isSelected = score === selectedScore
                      const isAllowed = canAdjust(score)
                      const profile = RISK_PROFILES[score]

                      return (
                        <button
                          key={score}
                          onClick={() => isAllowed && setSelectedScore(score)}
                          disabled={!isAllowed}
                          aria-pressed={isSelected}
                          aria-label={`רמת סיכון ${score} מתוך 5 — ${profile.name}${score === recommendedScore ? ' (מומלצת)' : ''}`}
                          className={`flex flex-col items-center p-2.5 lg:p-3 rounded-xl border-2 transition-all min-w-[52px] lg:min-w-[64px] ${
                            isSelected
                              ? 'border-blue-600 bg-blue-50 shadow-md scale-105'
                              : isAllowed
                              ? 'border-gray-200 bg-white hover:border-blue-300 hover:shadow cursor-pointer'
                              : 'border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed'
                          }`}
                        >
                          <span className={`text-xl lg:text-2xl font-bold ${isSelected ? 'text-blue-700' : isAllowed ? 'text-gray-700' : 'text-gray-400'}`}>
                            {score}
                          </span>
                          <span className={`text-[10px] lg:text-xs mt-1 ${isSelected ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
                            {profile.name}
                          </span>
                          {score === recommendedScore && (
                            <span className="text-[9px] lg:text-[10px] text-indigo-500 font-medium mt-0.5">מומלצת</span>
                          )}
                        </button>
                      )
                    })}
                  </div>

                  {/* Adjustment info */}
                  {adjustment !== 0 && (
                    <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-sm text-amber-800 flex items-center justify-center gap-2">
                      {adjustment > 0 ? <ArrowUp size={14} aria-hidden="true" /> : <ArrowDown size={14} aria-hidden="true" />}
                      <span>{adjustment > 0 ? 'העלאה' : 'הורדה'} בדרגה אחת — מ-{recommendedScore} ל-{selectedScore}</span>
                    </div>
                  )}
                </div>

                {/* Selected profile info */}
                {selectedProfile && (
                  <div className={`rounded-xl border p-4 lg:p-5 ${selectedProfile.color}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {selectedProfile.icon === 'shield'
                        ? <ShieldCheck size={18} aria-hidden="true" />
                        : <TrendingUp size={18} aria-hidden="true" />}
                      <p className="font-bold text-base lg:text-lg">{selectedScore}/5 — {selectedProfile.name}</p>
                    </div>
                    <p className="text-sm">{selectedProfile.description}</p>
                  </div>
                )}

                {/* Contact Iftach */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 lg:p-4 text-center">
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <Phone size={14} aria-hidden="true" />
                    <span>שינוי של יותר מדרגה אחת? יש לפנות ליפתח ונגר</span>
                  </div>
                  <a
                    href="tel:0503976397"
                    className="text-sm text-blue-600 font-medium hover:underline mt-1 inline-block"
                  >
                    050-3976397
                  </a>
                </div>
              </div>

              {/* ═══ RIGHT COLUMN (desktop) / BOTTOM (mobile): Declaration & Signature ═══ */}
              <div className="p-5 lg:p-8 border-t lg:border-t-0 border-gray-200 space-y-4 lg:space-y-5">

                <h3 className="font-bold text-gray-800 text-base lg:text-lg">הצהרה וחתימה</h3>

                {/* Declaration text */}
                <div className="bg-gray-50 rounded-lg p-3 lg:p-4 text-sm text-gray-700 space-y-1.5 max-h-40 lg:max-h-52 overflow-y-auto">
                  <p className="font-medium">אני מאשר/ת כי:</p>
                  <p>1. מילאתי שאלון ליבון צרכים ורמת הסיכון המומלצת היא {recommendedScore} מתוך 5.</p>
                  <p>2. בחרתי לאשר רמת סיכון {selectedScore} מתוך 5 ({selectedProfile?.name}).</p>
                  <p>3. הובהר לי כי רמת הסיכון משפיעה על סוג ההמלצות ועל הרכב תיק ההשקעות.</p>
                  <p>4. שינוי בדרגה אחת בלבד אפשרי באישור עצמאי. שינוי גדול יותר מחייב פנייה ליועץ.</p>
                  <p>5. השקעות כרוכות בסיכון, לרבות אובדן חלק או כל ההון המושקע.</p>
                  <p>6. אישור זה תקף למשך 12 חודשים או עד למילוי שאלון חדש.</p>
                </div>

                {/* Confirmation checkbox */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="confirmed"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="mt-1 w-4 h-4 cursor-pointer"
                  />
                  <label htmlFor="confirmed" className="text-sm text-gray-700 cursor-pointer">
                    קראתי והבנתי את ההצהרה, ואני מאשר/ת את רמת הסיכון שנבחרה
                  </label>
                </div>

                {/* Canvas signature pad */}
                <div>
                  <span id="risk-signature-canvas-label" className="block text-sm font-medium text-gray-700 mb-2">
                    חתימה בכתב יד (לא חובה)
                  </span>
                  <SignaturePad
                    onSignatureChange={setSignatureDataUrl}
                    isEmpty={signatureEmpty}
                    setIsEmpty={setSignatureEmpty}
                  />
                </div>

                {/* Typed name — accessible alternative to drawing */}
                <div>
                  <label htmlFor="typedName" className="block text-sm font-medium text-gray-700 mb-1">
                    שם מלא (חלופה לחתימה) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="typedName"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="הזן/י שם מלא"
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ fontFamily: 'cursive' }}
                    aria-required="true"
                    aria-invalid={!!error && !typedName.trim()}
                    aria-describedby={error ? 'risk-error' : undefined}
                  />
                </div>

                {/* Error */}
                {error && (
                  <div id="risk-error" role="alert" className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                    <AlertCircle size={16} className="flex-shrink-0" aria-hidden="true" />
                    <span>{error}</span>
                  </div>
                )}

                {/* Submit */}
                <button
                  onClick={handleSign}
                  disabled={isSubmitting || !typedName.trim() || !confirmed}
                  className="w-full bg-blue-900 text-white py-3 lg:py-3.5 rounded-lg font-semibold text-base lg:text-lg hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'שולח...' : 'חתום ואשר רמת סיכון'}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  החתימה הדיגיטלית תישמר עם חותמת זמן וכתובת IP לצרכי תיעוד משפטי
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Saving overlay */}
      {isSubmitting && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 mx-4 flex flex-col items-center gap-3">
            <div className="spinner w-10 h-10" />
            <p className="font-medium text-gray-900">שומר אישור רמת סיכון...</p>
          </div>
        </div>
      )}
    </div>
  )
}
